import { useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";
import { extractSummaryFromResponse } from "../../translator/utils/utils";
import type { TranscriptItem } from "../../translator/hooks/useTranslator";

export interface Meeting {
  _id: string | number;
  title: string;
  source_lang: string;
  target_lang: string;
  user_id: string;
  created_at: string;
  duration?: string | number;
  duration_seconds?: number;
  duration_ms?: number;
  dur?: string | number;
  segs?: number;
  mode?: string;
  [key: string]: any;
}

function formatHttpError(res: Response, defaultEs: string, defaultEn: string, isEn: boolean): string {
  if (res.status === 404) {
    return isEn
      ? "Meeting not found on the server."
      : "La minuta no fue encontrada en el servidor.";
  }
  if (res.status === 401 || res.status === 403) {
    return isEn
      ? "Session expired or unauthorized."
      : "Sesión expirada o sin permisos de acceso.";
  }
  if (res.status >= 500) {
    return isEn
      ? "Server error. Please try again later."
      : "Error interno del servidor. Inténtalo más tarde.";
  }
  return isEn ? `${defaultEn} (${res.status})` : `${defaultEs} (${res.status})`;
}

export function useMeetingHistory(translatorServiceUrl: string) {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [loadingMeetingId, setLoadingMeetingId] = useState<string | number | null>(null);

  const clearMeetings = useCallback(() => setMeetings([]), []);

  const fetchMeetings = useCallback(async (
    query: string = "",
    options?: {
      folderId?: string | number | null;
      tags?: string | string[];
      tagMode?: "and" | "or";
      isEn?: boolean;
      silent?: boolean;
    } | boolean,
    silentLegacy?: boolean
  ) => {
    let folderId: string | number | null | undefined = undefined;
    let tags: string | string[] | undefined = undefined;
    let tagMode: "and" | "or" | undefined = undefined;
    let isEn = false;
    let silent = false;

    if (typeof options === "boolean") {
      isEn = options;
      silent = !!silentLegacy;
    } else if (options && typeof options === "object") {
      folderId = options.folderId;
      tags = options.tags;
      tagMode = options.tagMode;
      isEn = !!options.isEn;
      silent = !!options.silent;
    }

    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");

      const params = new URLSearchParams();
      if (query) params.append("search", query);
      if (folderId !== undefined && folderId !== null) {
        params.append("folder_id", String(folderId));
      }
      if (tags) {
        const tagsStr = Array.isArray(tags) ? tags.join(",") : tags;
        if (tagsStr) params.append("tags", tagsStr);
      }
      if (tagMode) {
        params.append("tag_mode", tagMode);
      }

      const queryStr = params.toString() ? `?${params.toString()}` : "";

      const res = await fetch(`${cleanedUrl}/meetings${queryStr}`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(formatHttpError(res, "Error al obtener la lista de reuniones", "Failed to retrieve meeting list", isEn));
      }

      const data = await res.json();
      setMeetings(data.meetings || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Failed to retrieve meeting list." : "Error al obtener la lista de reuniones."));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [translatorServiceUrl]);

  const renameMeeting = useCallback(async (meetingId: string | number, newTitle: string, isEn: boolean = false) => {
    if (!newTitle.trim()) return false;

    setRenaming(true);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });

      if (!res.ok) {
        throw new Error(formatHttpError(res, "Error al renombrar la reunión", "Rename failed", isEn));
      }

      setMeetings((prev) =>
        prev.map((m) => (m._id === meetingId ? { ...m, title: newTitle.trim() } : m))
      );
      window.dispatchEvent(new CustomEvent("Cora-Lingo:meetings-changed"));
      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error renaming meeting." : "Error al renombrar la reunión."));
      return false;
    } finally {
      setRenaming(false);
    }
  }, [translatorServiceUrl]);

  const moveMeetingToFolder = useCallback(async (meetingId: string | number, folderId: string | number | null, isEn: boolean = false) => {
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folder_id: folderId }),
      });

      if (!res.ok) {
        throw new Error(formatHttpError(res, "Error al mover la reunión de carpeta", "Failed to move meeting", isEn));
      }

      setMeetings((prev) =>
        prev.map((m) => (m._id === meetingId ? { ...m, folder_id: folderId } : m))
      );
      window.dispatchEvent(new CustomEvent("Cora-Lingo:meetings-changed"));
      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error moving meeting." : "Error al mover la reunión."));
      return false;
    }
  }, [translatorServiceUrl]);

  const updateMeetingTags = useCallback(async (meetingId: string | number, tags: string[], isEn: boolean = false) => {
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tags }),
      });

      if (!res.ok) {
        throw new Error(formatHttpError(res, "No se pudo actualizar las etiquetas", "Failed to update tags", isEn));
      }

      setMeetings((prev) =>
        prev.map((m) => (m._id === meetingId ? { ...m, tags } : m))
      );
      window.dispatchEvent(new CustomEvent("Cora-Lingo:meetings-changed"));
      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error updating tags." : "Error al actualizar etiquetas."));
      return false;
    }
  }, [translatorServiceUrl]);

  const loadMeetingTranscripts = useCallback(async (
    meeting: Meeting,
    recording: boolean,
    isEn: boolean,
    onLoad: (meetingId: string, transcript: TranscriptItem[], srcLang: string, tgtLang: string) => void
  ) => {
    if (recording) {
      toast.error(
        isEn
          ? "Please stop the active recording before loading a past meeting."
          : "Por favor, detén la grabación activa antes de cargar una reunión anterior."
      );
      return false;
    }

    setLoadingMeetingId(meeting._id);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meeting._id}/transcripts`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Failed to load transcripts: ${res.status} ${res.statusText}`
            : `Error al cargar transcripciones: ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      const rawTranscripts = data.transcripts || [];

      // Map backend fields (original_text, translated_text) to local fields (orig, tran)
      const mappedTranscripts: TranscriptItem[] = rawTranscripts.map((t: any) => {
        const textOrig = t.original_text || "";
        const isMic = textOrig.startsWith("🎤 Yo:");
        return {
          timestamp: t.timestamp || "",
          orig: textOrig,
          tran: t.translated_text || "",
          isMic,
        };
      });

      onLoad(
        String(meeting._id),
        mappedTranscripts,
        meeting.source_lang,
        meeting.target_lang
      );

      return true;
    } catch (err: any) {
      console.error(err);
      const msg = err.message || (isEn ? "Error loading transcripts." : "Error al cargar las transcripciones de la reunión.");
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoadingMeetingId(null);
    }
  }, [translatorServiceUrl]);

  const loadMeetingSummary = useCallback(async (
    meeting: Meeting,
    recording: boolean,
    isEn: boolean,
    onOpenSummary: (meeting: Meeting, summaryText: string, htmlFilename?: string) => void
  ) => {
    if (recording) {
      toast.error(isEn ? "Stop current recording before loading a meeting." : "Debes detener la grabación actual antes de cargar otra reunión.");
      return false;
    }

    try {
      setLoadingMeetingId(meeting._id);
      setError("");
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meeting._id}`, {
        method: "GET",
        headers: authHeaders,
      });
      if (res.ok) {
        const data = await res.json();
        const serverMeeting = data.meeting || (data.title ? data : null);
        const updatedMeeting = serverMeeting ? { ...meeting, ...serverMeeting } : meeting;
        setMeetings((prev) =>
          prev.map((m) => (m._id === meeting._id ? updatedMeeting : m))
        );
        const extracted = extractSummaryFromResponse(data, isEn ? "en" : "es");
        const summaryText = extracted.summary || data.summary || data.meeting?.summary || "";
        const htmlFilename = extracted.html_filename || data.html_filename || data.meeting?.html_filename || "";
        onOpenSummary(updatedMeeting, summaryText, htmlFilename);
        return true;
      } else {
        const msg = isEn ? "Error loading meeting details." : "Error al cargar los detalles de la reunión.";
        setError(msg);
        toast.error(msg);
        return false;
      }
    } catch (err: any) {
      console.error(err);
      const msg = isEn ? "Network error loading meeting details." : "Error de red al cargar los detalles de la reunión.";
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoadingMeetingId(null);
    }
  }, [translatorServiceUrl]);

  const deleteMeeting = useCallback(async (meetingId: string | number, isEn: boolean = false) => {
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(`Delete failed: ${res.status} ${res.statusText}`);
      }

      setMeetings((prev) => prev.filter((m) => m._id !== meetingId));
      window.dispatchEvent(new CustomEvent("Cora-Lingo:meetings-changed"));
      toast.success(isEn ? "Meeting deleted successfully." : "Reunión eliminada correctamente.");
      return true;
    } catch (err: any) {
      console.error("Delete meeting error:", err);
      const msg = isEn 
        ? "Error deleting the recording. Please try again later." 
        : "Error al borrar la grabación. Inténtalo más tarde.";
      setError(msg);
      toast.error(msg);
      return false;
    }
  }, [translatorServiceUrl]);

  return {
    meetings,
    loading,
    error,
    renaming,
    loadingMeetingId,
    setError,
    clearMeetings,
    fetchMeetings,
    renameMeeting,
    deleteMeeting,
    loadMeetingTranscripts,
    loadMeetingSummary,
    moveMeetingToFolder,
    updateMeetingTags,
  };
}
